import { useGetCampaignQueueStatusQuery } from '@api/scraperApi';

/**
 * Poll a campaign's queue position + ETA.
 *
 * Adaptive polling: 30s by default (matches CampaignDetails) but backs off
 * to 60s when the campaign is deep in the queue (`position > 50`) — those
 * positions don't change quickly and over-polling burns request budget.
 *
 * Stops polling entirely once the campaign has no pending tasks
 * (`position === 0`), since the data is static.
 *
 * Set `enabled` to false to skip the request entirely (e.g. while a
 * campaign is still in 'draft' status — the backend would 404 on missing
 * tasks anyway, but skipping avoids the noise).
 */
export const useCampaignQueueStatus = (campaignId: string | undefined, options: { enabled?: boolean } = {}) => {
  const enabled = options.enabled ?? Boolean(campaignId);
  const skipQuery = !enabled || !campaignId;

  // Adaptive interval — RTK Query lets us recompute on each render. Start
  // optimistic at 30s; we adjust below using the previous response.
  const queryResult = useGetCampaignQueueStatusQuery(skipQuery ? '' : (campaignId as string), {
    skip: skipQuery,
    pollingInterval: 30_000,
    refetchOnMountOrArgChange: true,
  });

  // Once we have data, decide whether to slow polling down. RTK Query
  // doesn't expose a hook to mutate pollingInterval mid-flight without
  // unsubscribing, so this is a soft signal exposed to the caller — the
  // CampaignDetails page can use it for adaptive UI hints.
  const {data} = queryResult;
  const recommendedNextPollMs =
    data && data.position > 50
      ? 60_000
      : data && data.position === 0
        ? 0 // stop polling — campaign has no pending work
        : 30_000;

  return {
    ...queryResult,
    recommendedNextPollMs,
  };
};
